/**
 * Company detail page — opens when a card on CompaniesPage is clicked.
 * Mirrors InvestorDetailPage shape: header with key facts, tabs for
 * Profile / Research / CRS / Matches / Notes, plus the existing
 * ResearchSourcesPanel and a completion-checklist tooltip.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Building2,
  ArrowLeft,
  Search,
  Globe,
  MapPin,
  Briefcase,
  Mail,
  Users,
  ExternalLink,
  Sparkles,
  FileText,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Pencil,
  UploadCloud,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import DualScoreChip from '../components/DualScoreChip';
import ResearchSourcesPanel from '../components/ResearchSourcesPanel';
import RubricAssessmentPanel from '../components/RubricAssessmentPanel';
import PitchDeckTab from '../components/PitchDeckTab';
import CRSRadarChart from '../components/CRSRadarChart';
import CRSTierBadge from '../components/CRSTierBadge';
import MomentumBadge from '../components/MomentumBadge';
import api from '../services/api';
import { appendAuthToken } from '../services/auth';
import capitalReadinessService from '../services/capitalReadinessService';
import momentumService from '../services/momentumService';
import matchService from '../services/matchService';
import MatchCard from '../components/MatchCard';
import CohortBenchmarkTab from '../components/CohortBenchmarkTab';
import { CapitalReadinessScore } from '../types/capitalReadiness';
import { DealMomentum } from '../types/dealMomentum';
import { Match } from '../types/match';

interface Company {
  id: number;
  name: string;
  website?: string | null;
  industry?: string | null;
  sub_industry?: string | null;
  description?: string | null;
  headquarters?: string | null;
  employee_count?: number | null;
  contact_email?: string | null;
  contact_name?: string | null;
  funding_stage?: string | null;
  funding_amount_sought?: number | null;
  current_revenue?: number | null;
  monthly_revenue?: number | null;
  revenue_growth_rate?: number | null;
  gross_margin?: number | null;
  burn_rate?: number | null;
  runway_months?: number | null;
  customer_acquisition_cost?: number | null;
  lifetime_value?: number | null;
  valuation?: number | null;
  is_currently_raising?: boolean | null;
  previous_funding?: Record<string, any> | null;
  previous_funding_raw?: string | null;
  pitch_deck_url?: string | null;
  pitch_deck_available?: boolean | null;
  pitch_deck_extracted_at?: string | null;
  pitch_deck_analysis_json?: PitchDeckAnalysis | null;
  pitch_deck_analyzed_at?: string | null;
  founder_demographics?: string | null;
  university_affiliations?: string | null;
  executive_summary?: string | null;
  location_state?: string | null;
  group_name?: string | null;
  team_size?: number | null;
  ai_company_dossier?: string | null;
  dossier_generated_at?: string | null;
  research_status?: string | null;
  last_researched_at?: string | null;
  research_completion_pct?: number | null;
  rubric_score?: number | null;
  rubric_tier?: string | null;
  rubric_assessment_at?: string | null;
  tags?: string[] | null;
  created_at?: string | null;
}

interface ProvenanceRow {
  source_type: string;
  source_url: string | null;
  value: string | null;
  written_at: string | null;
  written_by: string;
}

interface ProvenanceAttempt {
  source: string;
  attempted_at: string | null;
  outcome:
    | 'not_attempted'
    | 'no_match'
    | 'match_found_but_field_absent'
    | 'error'
    | 'found';
  detail: string;
  url: string | null;
}

interface MissingItem {
  key: string;
  label: string;
  weight: number;
  provenance: ProvenanceAttempt[];
}

interface CompletionResponse {
  percent: number;
  free_percent: number;
  free_max: number;
  earned: number;
  // New shape — array of objects with provenance.
  missing: MissingItem[];
  // Back-compat string list for older UI builds.
  missing_labels?: string[];
  checklist: Array<{
    key: string;
    label: string;
    weight: number;
    present: boolean;
    source_class: string;
    filled_by?: ProvenanceRow | null;
  }>;
}

// Color + glyph per provenance outcome — matches the existing
// completion-tab visual language (green ✓ for present, gray for absent).
const OUTCOME_STYLES: Record<
  ProvenanceAttempt['outcome'],
  { glyph: string; bg: string; text: string; ring: string; label: string }
> = {
  found: {
    glyph: '✓',
    bg: 'bg-green-500',
    text: 'text-green-700',
    ring: 'ring-green-200',
    label: 'Found',
  },
  match_found_but_field_absent: {
    glyph: '!',
    bg: 'bg-amber-400',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    label: 'Matched but field absent',
  },
  no_match: {
    glyph: '✗',
    bg: 'bg-red-500',
    text: 'text-red-700',
    ring: 'ring-red-200',
    label: 'No match',
  },
  not_attempted: {
    glyph: '–',
    bg: 'bg-gray-400',
    text: 'text-gray-600',
    ring: 'ring-gray-200',
    label: 'Not attempted',
  },
  error: {
    glyph: '!',
    bg: 'bg-red-600',
    text: 'text-red-800',
    ring: 'ring-red-200',
    label: 'Error',
  },
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  sec_iapd_individual:       'SEC IAPD',
  sec_iapd_firm:             'SEC IAPD (firm)',
  finra_brokercheck:         'FINRA BrokerCheck',
  angellist:                 'AngelList',
  wikipedia:                 'Wikipedia',
  opencorporates:            'OpenCorporates',
  opensecrets:               'OpenSecrets',
  hunter_io:                 'Hunter.io',
  claude_sentiment:          'Claude (sentiment)',
  firm_enrichment_claude:    'Claude (firm enrichment)',
  company_enrichment_claude: 'Claude (company enrichment)',
};

const formatSourceLabel = (sourceType: string): string =>
  SOURCE_TYPE_LABELS[sourceType] || sourceType.replace(/_/g, ' ');

interface PitchDeckAnalysis {
  strengths?: string[];
  areas_of_growth?: string[];
  narrative_clarity_1_5?: number;
  market_signal_1_5?: number;
  traction_evidence_1_5?: number;
  ask_clarity_1_5?: number;
  team_signal_1_5?: number;
  overall_band_1_5?: number;
  headline?: string;
  recommended_next_step?: string;
  model_used?: string;
  analyzed_at?: string;
}

type Tab = 'profile' | 'cohort' | 'financials' | 'reports' | 'matches' | 'research' | 'rubric' | 'crs' | 'pitch_deck' | 'completion' | 'dossier';


// Inline editor for the Group cell on the company detail page.
// Click "edit" → datalist of existing groups + free-text input → save
// hits PUT /companies/{id} with the new group_name. Pass null to clear.
const InlineGroupEditor: React.FC<{
  companyId: number;
  currentGroup: string | null;
  onChanged: () => Promise<void>;
}> = ({ companyId, currentGroup, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentGroup || '');
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(currentGroup || ''); }, [currentGroup]);

  useEffect(() => {
    if (!editing || groupOptions.length > 0) return;
    api.get('/company-groups').then((r) => {
      setGroupOptions((r.data?.groups || []).map((g: any) => g.name));
    }).catch(() => {});
  }, [editing, groupOptions.length]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}`, {
        group_name: draft.trim() || null,
      });
      toast.success(draft.trim() ? `Moved to "${draft.trim()}"` : 'Removed from group');
      await onChanged();
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-gray-900">{currentGroup || '—'}</p>
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-blue-600 hover:underline"
        >
          edit
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        list="group-options"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Group name…"
        disabled={saving}
        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        autoFocus
      />
      <datalist id="group-options">
        {groupOptions.map((g) => <option key={g} value={g} />)}
      </datalist>
      <button
        onClick={save}
        disabled={saving}
        className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
      >
        {saving ? '…' : 'Save'}
      </button>
      <button
        onClick={() => { setEditing(false); setDraft(currentGroup || ''); }}
        disabled={saving}
        className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
      >
        Cancel
      </button>
    </div>
  );
};

// Tab order — flow goes top-down by user priority. The four
// "story" tabs (dossier, pitch deck, matches, rubric) cluster up
// front so an admin can read a company's narrative without
// hunting; analytical and admin tabs follow.
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'profile',    label: 'Profile' },
  { key: 'dossier',    label: 'Dossier' },
  { key: 'pitch_deck', label: 'Pitch Deck' },
  { key: 'matches',    label: 'Matches' },
  { key: 'crs',        label: 'Capital Readiness' },
  { key: 'cohort',     label: 'Cohort Benchmark' },
  { key: 'rubric',     label: 'Rubric' },
  { key: 'reports',    label: 'Reports' },
  { key: 'financials', label: 'Financials' },
  { key: 'research',   label: 'Research Sources' },
  { key: 'completion', label: 'Completion' },
];

interface AnalysisRow {
  id: number;
  status: string;
  analysis_type: string;
  started_at?: string | null;
  completed_at?: string | null;
  overall_score?: number | null;
  recommendation?: string | null;
  progress_percentage?: number | null;
}

const RECOMMENDATION_BADGE: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  strong_yes:              { label: 'STRONG YES',  color: 'bg-green-100 text-green-800 border-green-200',   icon: CheckCircle2 },
  yes:                     { label: 'YES',         color: 'bg-blue-100 text-blue-800 border-blue-200',      icon: CheckCircle2 },
  yes_if_conditions_met:   { label: 'YES IF',      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',icon: AlertCircle },
  pass_with_feedback:      { label: 'PASS (FB)',   color: 'bg-orange-100 text-orange-800 border-orange-200',icon: XCircle },
  hard_pass:               { label: 'HARD PASS',   color: 'bg-red-100 text-red-800 border-red-200',         icon: XCircle },
};

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString() : '—';

const formatMoney = (n?: number | null) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = Number(id);

  const [company, setCompany] = useState<Company | null>(null);
  const [crs, setCrs] = useState<CapitalReadinessScore | null>(null);
  const [momentum, setMomentum] = useState<DealMomentum | null>(null);
  const [completion, setCompletion] = useState<CompletionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [researchTriggering, setResearchTriggering] = useState(false);
  const [analyses, setAnalyses] = useState<AnalysisRow[]>([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [generatingMatches, setGeneratingMatches] = useState(false);
  // Track which missing-item keys have their provenance panel expanded.
  const [expandedMissing, setExpandedMissing] = useState<Set<string>>(new Set());

  const toggleMissing = (key: string) => {
    setExpandedMissing((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const loadAnalyses = async () => {
    setAnalysisLoading(true);
    try {
      const res = await api.get(`/companies/${companyId}/analyses`);
      setAnalyses(res.data?.analyses || []);
    } catch {
      setAnalyses([]);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleRunAnalysis = async () => {
    setRunningAnalysis(true);
    let analysisId: number | null = null;
    try {
      const res = await api.post('/analysis', {
        company_id: companyId,
        analysis_type: 'standard',
      });
      analysisId = res.data?.id ?? null;
      toast.success(
        'Analysis queued. The 6-expert run takes ~1-3 minutes; we will keep watching.',
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to queue analysis');
      setRunningAnalysis(false);
      return;
    }

    // Adaptive polling. The backend has no per-stage progress — every
    // IN_PROGRESS analysis reports 50% — so we just need to detect
    // when status flips to COMPLETED or FAILED and refresh the list.
    // Hard cap at 5 minutes so a stuck run doesn't poll forever.
    const startedAt = Date.now();
    const HARD_CAP_MS = 5 * 60 * 1000;
    const intervalFor = (elapsedMs: number): number => {
      if (elapsedMs < 30_000) return 4_000;
      if (elapsedMs < 90_000) return 8_000;
      return 15_000;
    };

    const poll = async (): Promise<void> => {
      const elapsed = Date.now() - startedAt;
      if (elapsed > HARD_CAP_MS) {
        await loadAnalyses();
        toast.error(
          'Analysis took longer than 5 minutes. It may still be running — refresh later.',
        );
        setRunningAnalysis(false);
        return;
      }

      try {
        if (analysisId) {
          const r = await api.get(`/analysis/${analysisId}`);
          const status = r.data?.status;
          if (status === 'completed') {
            await loadAnalyses();
            toast.success(
              `Analysis #${analysisId} complete${
                r.data?.overall_score != null
                  ? ` — overall score ${Math.round(r.data.overall_score)}`
                  : ''
              }.`,
            );
            setRunningAnalysis(false);
            return;
          }
          if (status === 'failed') {
            await loadAnalyses();
            toast.error(
              `Analysis #${analysisId} failed${
                r.data?.error_message ? `: ${r.data.error_message}` : '.'
              }`,
              { duration: 8000 },
            );
            setRunningAnalysis(false);
            return;
          }
        }
        // Refresh the list so the user sees the row update from
        // pending → in_progress → completed.
        await loadAnalyses();
      } catch {
        // Transient — keep polling.
      }
      setTimeout(poll, intervalFor(Date.now() - startedAt));
    };
    setTimeout(poll, intervalFor(0));
  };

  const loadMatches = async (cid: number) => {
    setMatchesLoading(true);
    const result = await matchService.fetchCompanyMatches(cid);
    if (result.error) {
      toast.error(result.error);
      setMatches([]);
    } else {
      // Sort by overall_score desc (backend already does this, but be safe).
      const sorted = [...(result.data || [])].sort(
        (a, b) => (b.overall_score ?? 0) - (a.overall_score ?? 0)
      );
      setMatches(sorted);
    }
    setMatchesLoading(false);
    setMatchesLoaded(true);
  };

  const handleGenerateCompanyMatches = async () => {
    if (!company) return;
    setGeneratingMatches(true);
    const result = await matchService.generateMatches({ startup_id: company.id } as any);
    setGeneratingMatches(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data?.skipped_reason === 'below_completion_threshold') {
      const pct = result.data.completion_pct ?? 0;
      const thr = result.data.completion_threshold ?? 80;
      toast(
        `Matching is gated until research completion reaches ${thr}% — ` +
          `this company is at ${pct}%. Run more research depth to enable matching.`,
        { icon: '⚠️', duration: 7000 },
      );
      return;
    }
    toast.success(`Generated ${result.data?.generated ?? 0} match(es)`);
    await loadMatches(company.id);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [coRes, crsListRes, momentumRes, compRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        capitalReadinessService.fetchScores({ size: 500 }).then((r) => r),
        momentumService.fetchSignals().catch(() => ({ data: [] as DealMomentum[] })),
        api.get(`/companies/${companyId}/completion`).catch(() => null),
      ]);
      setCompany(coRes.data);
      const matchedCrs = (crsListRes.data?.scores || []).find((s) => s.startup_id === companyId);
      setCrs(matchedCrs || null);
      const matchedMom = ((momentumRes as any).data || []).find((m: DealMomentum) => m.startup_id === companyId);
      setMomentum(matchedMom || null);
      if (compRes?.data) setCompletion(compRes.data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Number.isFinite(companyId)) {
      loadAll();
      loadAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (activeTab !== 'matches' || matchesLoaded || !company) return;
    loadMatches(company.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, matchesLoaded, company?.id]);

  const triggerResearch = async () => {
    setResearchTriggering(true);
    try {
      await api.post(`/companies/${companyId}/research`);
      toast.success('Deep-dive research queued');
      // Soft refresh after a moment so the user sees status changes
      setTimeout(loadAll, 2000);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Research trigger failed');
    } finally {
      setResearchTriggering(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading company…
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <Building2 className="w-10 h-10 mx-auto text-blue-600 mb-4" />
          <h2 className="text-lg font-semibold mb-2">Company not found</h2>
          <Link to="/companies" className="text-sm text-blue-600 hover:underline">
            ← Back to companies
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back link */}
        <button
          onClick={() => navigate('/companies')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to companies
        </button>

        {/* Header card */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
                <DualScoreChip
                  completionPct={company.research_completion_pct ?? null}
                  rubricScore={company.rubric_score ?? null}
                  rubricTier={company.rubric_tier ?? null}
                />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                {company.industry && (
                  <span className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    <Briefcase className="w-3 h-3 mr-1" />
                    {company.industry}
                  </span>
                )}
                {company.headquarters && (
                  <span className="inline-flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {company.headquarters}
                  </span>
                )}
                {company.website && (
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:underline"
                  >
                    <Globe className="w-3 h-3 mr-1" />
                    {company.website.replace(/^https?:\/\//, '')}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
                {company.contact_email && (
                  <a
                    href={`mailto:${company.contact_email}`}
                    className="inline-flex items-center text-gray-700 hover:text-gray-900"
                  >
                    <Mail className="w-3 h-3 mr-1" />
                    {company.contact_email}
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {crs && (
                <Link
                  to={`/capital-readiness/scores/${crs.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-200 bg-blue-50 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-100"
                >
                  CRS <span className="font-bold">{Math.round(crs.overall_score)}</span>
                  <CRSTierBadge tier={crs.tier} size="sm" />
                </Link>
              )}
              {momentum && (
                <Link to={`/momentum/startup/${company.id}`}>
                  <MomentumBadge tier={momentum.momentum_tier} size="sm" />
                </Link>
              )}
              <a
                href={appendAuthToken(`${process.env.REACT_APP_API_URL || '/api/v1'}/companies/${company.id}/report.pdf`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                title="Download a printable PDF profile of this company"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                Download PDF
              </a>
              <button
                onClick={triggerResearch}
                disabled={researchTriggering}
                className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
                title="Re-run the deep-dive research pipeline"
              >
                {researchTriggering ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Search className="w-4 h-4 mr-1.5" />
                )}
                Research
              </button>
              <Link
                to={`/companies/upload-brochure?company_id=${company.id}`}
                className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
                title="Upload a pitch deck or brochure to auto-fill profile fields"
              >
                <UploadCloud className="w-4 h-4 mr-1.5" />
                Upload brochure
              </Link>
              <button
                onClick={handleRunAnalysis}
                disabled={runningAnalysis}
                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                title="Run the 6-expert AI analysis on this company"
              >
                {runningAnalysis ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-1.5" />
                )}
                Run new analysis
              </button>
              <Link
                to={`/companies/new?id=${company.id}`}
                className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Edit
              </Link>
            </div>
          </div>

          {/* Quick facts row */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Funding stage</p>
              <p className="text-sm font-medium text-gray-900">{company.funding_stage || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Currently raising</p>
              <p className="text-sm font-medium text-gray-900">{formatMoney(company.funding_amount_sought)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Funding to date</p>
              <p className="text-sm font-medium text-gray-900">{company.previous_funding_raw || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Annual revenue</p>
              <p className="text-sm font-medium text-gray-900">{formatMoney(company.current_revenue)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Monthly revenue</p>
              <p className="text-sm font-medium text-gray-900">{formatMoney(company.monthly_revenue)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Team</p>
              <p className="text-sm font-medium text-gray-900">
                {company.team_size || company.employee_count || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Group</p>
              <InlineGroupEditor
                companyId={company.id}
                currentGroup={company.group_name || null}
                onChanged={async () => {
                  // Refresh company so display reflects the new group.
                  const r = await api.get(`/companies/${company.id}`);
                  if (r.data) setCompany(r.data);
                }}
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Pitch deck</p>
              <p className="text-sm font-medium text-gray-900">
                {company.pitch_deck_url && company.pitch_deck_available !== false ? (
                  <a
                    href={company.pitch_deck_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Open
                  </a>
                ) : '—'}
              </p>
            </div>
          </div>

          {(company.executive_summary || company.founder_demographics || company.university_affiliations) && (
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
              {company.executive_summary && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Executive summary</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {company.executive_summary}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {company.founder_demographics && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Founder demographics</p>
                    <p className="text-sm text-gray-800">{company.founder_demographics}</p>
                  </div>
                )}
                {company.university_affiliations && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">University affiliations</p>
                    <p className="text-sm text-gray-800">{company.university_affiliations}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Completion bar */}
          {completion && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                <span>Research completion</span>
                <span className="font-medium text-gray-900">{completion.percent}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full ${completion.percent >= 80 ? 'bg-green-500' : completion.percent >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
                  style={{ width: `${completion.percent}%` }}
                />
              </div>
              {completion.missing.length > 0 && (
                <p className="text-xs text-gray-500 mt-1.5">
                  Missing: {completion.missing.slice(0, 4).map((m) => m.label).join(' · ')}
                  {completion.missing.length > 4 && ` · +${completion.missing.length - 4} more`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="border-b border-gray-200 flex overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <div className="space-y-4 text-sm text-gray-800">
                {company.description ? (
                  <p className="leading-relaxed whitespace-pre-wrap">{company.description}</p>
                ) : (
                  <p className="text-gray-500 italic">No description on file. Run research to populate.</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100 text-xs text-gray-600">
                  <div>
                    <p className="uppercase tracking-wide text-gray-400">Sub-industry</p>
                    <p className="text-gray-900">{company.sub_industry || '—'}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wide text-gray-400">Contact name</p>
                    <p className="text-gray-900">{company.contact_name || '—'}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wide text-gray-400">Last researched</p>
                    <p className="text-gray-900">{formatDate(company.last_researched_at)}</p>
                  </div>
                </div>
                {company.tags && company.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-3">
                    {company.tags.map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'cohort' && (
              <CohortBenchmarkTab
                companyId={company.id}
                groupName={company.group_name}
              />
            )}

            {activeTab === 'financials' && (() => {
              const fin = company;
              const hasAnyFinancials =
                fin.current_revenue != null ||
                fin.monthly_revenue != null ||
                fin.revenue_growth_rate != null ||
                fin.gross_margin != null ||
                fin.burn_rate != null ||
                fin.runway_months != null ||
                fin.customer_acquisition_cost != null ||
                fin.lifetime_value != null ||
                fin.funding_stage != null ||
                fin.funding_amount_sought != null ||
                fin.valuation != null ||
                fin.previous_funding_raw != null ||
                (fin.previous_funding && Object.keys(fin.previous_funding).length > 0) ||
                fin.is_currently_raising != null;

              const formatPct = (n?: number | null, asDecimal = false) => {
                if (n == null) return '—';
                const pct = asDecimal ? n * 100 : n;
                return `${pct.toFixed(1)}%`;
              };

              const formatRunway = (n?: number | null) =>
                n == null ? '—' : `${Math.round(n)} mo`;

              const formatPrevFunding = () => {
                if (fin.previous_funding_raw) return fin.previous_funding_raw;
                if (fin.previous_funding && Object.keys(fin.previous_funding).length > 0) {
                  return Object.entries(fin.previous_funding)
                    .map(([k, v]) => `${k}: ${typeof v === 'number' ? formatMoney(v) : String(v)}`)
                    .join(' · ');
                }
                return '—';
              };

              const formatRaising = (b?: boolean | null) =>
                b == null ? '—' : b ? 'Yes' : 'No';

              const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
                <div>
                  <p className="uppercase tracking-wide text-gray-400 text-xs">{label}</p>
                  <p className="text-gray-900 text-sm">{value}</p>
                </div>
              );

              if (!hasAnyFinancials) {
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
                    No financial data on file. Run research or import via partner CSV.
                  </div>
                );
              }

              return (
                <div className="space-y-6 text-sm text-gray-800">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Revenue</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-gray-100 text-xs text-gray-600">
                      <Stat label="Annual revenue" value={formatMoney(fin.current_revenue)} />
                      <Stat label="Monthly revenue" value={formatMoney(fin.monthly_revenue)} />
                      <Stat label="Growth rate" value={formatPct(fin.revenue_growth_rate)} />
                      <Stat label="Gross margin" value={formatPct(fin.gross_margin, true)} />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Capital</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2 border-t border-gray-100 text-xs text-gray-600">
                      <Stat label="Currently raising" value={formatRaising(fin.is_currently_raising)} />
                      <Stat label="Funding amount sought" value={formatMoney(fin.funding_amount_sought)} />
                      <Stat label="Valuation" value={formatMoney(fin.valuation)} />
                      <Stat label="Funding stage" value={fin.funding_stage || '—'} />
                      <div className="md:col-span-2">
                        <p className="uppercase tracking-wide text-gray-400 text-xs">Previous funding</p>
                        <p className="text-gray-900 text-sm whitespace-pre-wrap">{formatPrevFunding()}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Unit economics / Operations</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-gray-100 text-xs text-gray-600">
                      <Stat label="Burn rate ($/mo)" value={formatMoney(fin.burn_rate)} />
                      <Stat label="Runway" value={formatRunway(fin.runway_months)} />
                      <Stat label="CAC" value={formatMoney(fin.customer_acquisition_cost)} />
                      <Stat label="LTV" value={formatMoney(fin.lifetime_value)} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {activeTab === 'reports' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Investment Readiness Reports</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    6-expert AI synthesis (market · financial · technical · GTM · team · ESG) producing
                    a composite recommendation with investment thesis, value drivers, gaps, and risk
                    mitigation. Every run leverages this company's enrichment data — research sources,
                    CRS, rubric, dossier — as input. Use the <strong>Run new analysis</strong> button
                    in the header to kick off a new run.
                  </p>
                </div>

                {analysisLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 py-4 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
                  </div>
                ) : analyses.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
                    No analysis reports for this company yet. Click <strong>Run new analysis</strong> to generate one.
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white overflow-hidden">
                    {analyses.map((a) => {
                      const recCfg = a.recommendation
                        ? RECOMMENDATION_BADGE[a.recommendation.toLowerCase()]
                        : undefined;
                      const RecIcon = recCfg?.icon;
                      return (
                        <li key={a.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <Link
                                to={`/detailed-report/${a.id}`}
                                className="text-sm font-medium text-gray-900 hover:text-blue-700"
                              >
                                Analysis #{a.id}
                              </Link>
                              <span className="text-xs uppercase tracking-wide text-gray-400">
                                {a.analysis_type}
                              </span>
                              {a.status !== 'completed' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
                                  a.status === 'failed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {a.status === 'in_progress' || a.status === 'pending' ? (
                                    <>
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                      {a.status === 'pending' ? 'Queued' : 'Running 6 experts…'}
                                    </>
                                  ) : (
                                    <>{a.status}</>
                                  )}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {a.completed_at
                                ? `Completed ${new Date(a.completed_at).toLocaleString()}`
                                : a.started_at
                                  ? `Started ${new Date(a.started_at).toLocaleString()}`
                                  : 'Queued'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            {a.overall_score != null && (
                              <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                                {Math.round(a.overall_score)}
                              </span>
                            )}
                            {recCfg && RecIcon && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${recCfg.color}`}>
                                <RecIcon className="w-3 h-3" />
                                {recCfg.label}
                              </span>
                            )}
                            <Link
                              to={`/detailed-report/${a.id}`}
                              className="text-xs text-blue-700 hover:underline ml-2"
                            >
                              Open →
                            </Link>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {activeTab === 'matches' && (
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Matches</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Top scoring investors for this company, sorted by match score.
                    </p>
                  </div>
                  <button
                    onClick={handleGenerateCompanyMatches}
                    disabled={generatingMatches}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {generatingMatches ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Generate matches
                      </>
                    )}
                  </button>
                </div>

                {matchesLoading ? (
                  <div className="flex items-center text-sm text-gray-600 py-4">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading matches...
                  </div>
                ) : matches.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
                    No matches yet for this company. Click <strong>Generate matches</strong> to run the matcher.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {matches.map((m) => (
                      <MatchCard key={m.id} match={m} hideTarget />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'research' && (
              <ResearchSourcesPanel entityKind="company" entityId={company.id} />
            )}

            {activeTab === 'rubric' && (
              <RubricAssessmentPanel entityKind="company" entityId={company.id} />
            )}

            {activeTab === 'pitch_deck' && (
              <PitchDeckTab
                companyId={company.id}
                deckUrl={company.pitch_deck_url || null}
                deckAvailable={company.pitch_deck_available ?? null}
                analysis={company.pitch_deck_analysis_json || null}
                analyzedAt={company.pitch_deck_analyzed_at || null}
                onReanalyze={async () => {
                  try {
                    await api.post(`/companies/${company.id}/pitch-deck/analyze`);
                    toast.success('Pitch deck analysis queued — refresh in ~30s.');
                  } catch (e: any) {
                    toast.error(e?.response?.data?.detail || 'Failed to queue analysis');
                  }
                }}
                onUploaded={loadAll}
              />
            )}

            {activeTab === 'crs' && (
              <div>
                {crs ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Capital Readiness Score</p>
                        <p className="text-3xl font-bold text-gray-900">{Math.round(crs.overall_score)}</p>
                      </div>
                      <CRSTierBadge tier={crs.tier} size="md" />
                      <Link
                        to={`/capital-readiness/scores/${crs.id}`}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Full detail →
                      </Link>
                    </div>
                    {crs.dimension_scores && (
                      <div className="flex justify-center pt-4">
                        <CRSRadarChart scores={crs.dimension_scores as any} size={280} />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No Capital Readiness Score yet. Click <strong>Research</strong> above to compute one (free, deterministic).
                  </p>
                )}
              </div>
            )}

            {activeTab === 'completion' && (
              <div>
                {completion ? (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-900">Research checklist</span>
                      <span className="text-gray-600">
                        {completion.earned} / 100 (free max: {completion.free_max})
                      </span>
                    </div>

                    {completion.missing.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-amber-900">
                            Missing items ({completion.missing.length}) — click to audit what we tried
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {completion.missing.map((m) => {
                            const isOpen = expandedMissing.has(m.key);
                            return (
                              <li
                                key={m.key}
                                className="bg-white border border-amber-100 rounded"
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleMissing(m.key)}
                                  className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-amber-50/50"
                                >
                                  <span className="flex items-center gap-2 min-w-0">
                                    <span className="text-amber-600 text-xs">{isOpen ? '▾' : '▸'}</span>
                                    <span className="text-gray-900 truncate">{m.label}</span>
                                  </span>
                                  <span className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-xs text-gray-500">
                                      {m.provenance.length} {m.provenance.length === 1 ? 'attempt' : 'attempts'}
                                    </span>
                                    <span className="text-xs text-gray-500">{m.weight}%</span>
                                  </span>
                                </button>
                                {isOpen && (
                                  <div className="border-t border-amber-100 px-3 py-2 bg-amber-50/30">
                                    {m.provenance.length === 0 ? (
                                      <p className="text-xs text-gray-500 italic">
                                        No per-source provenance available for this item — it's
                                        composite (e.g. number of sources, dossier) and not
                                        attributable to any single source.
                                      </p>
                                    ) : (
                                      <ul className="space-y-1.5">
                                        {m.provenance.map((p, idx) => {
                                          const style = OUTCOME_STYLES[p.outcome] || OUTCOME_STYLES.not_attempted;
                                          return (
                                            <li
                                              key={`${p.source}-${idx}`}
                                              className="flex items-start gap-2 text-xs"
                                            >
                                              <span
                                                className={`inline-block w-4 h-4 rounded-full ${style.bg} text-white text-[10px] leading-4 text-center flex-shrink-0 mt-0.5`}
                                                title={style.label}
                                              >
                                                {style.glyph}
                                              </span>
                                              <span className="flex-1 min-w-0">
                                                <span className="flex flex-wrap items-baseline gap-x-2">
                                                  <span className="font-medium text-gray-900">
                                                    {formatSourceLabel(p.source)}
                                                  </span>
                                                  <span className={`uppercase tracking-wide text-[10px] ${style.text}`}>
                                                    {style.label}
                                                  </span>
                                                  {p.attempted_at && (
                                                    <span className="text-gray-400">
                                                      {new Date(p.attempted_at).toLocaleString()}
                                                    </span>
                                                  )}
                                                </span>
                                                <span className="block text-gray-600 mt-0.5">
                                                  {p.detail}
                                                </span>
                                                {p.url && (
                                                  <a
                                                    href={p.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline mt-0.5"
                                                  >
                                                    {p.url}
                                                    <ExternalLink className="w-3 h-3" />
                                                  </a>
                                                )}
                                              </span>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    <ul className="divide-y divide-gray-100">
                      {completion.checklist.map((item) => (
                        <li
                          key={item.key}
                          className="flex items-center justify-between py-2 text-sm gap-3"
                        >
                          <span className="flex items-center gap-2 min-w-0 flex-1">
                            {item.present ? (
                              <span className="inline-block w-4 h-4 rounded-full bg-green-500 text-white text-[10px] leading-4 text-center flex-shrink-0">
                                ✓
                              </span>
                            ) : (
                              <span className="inline-block w-4 h-4 rounded-full bg-gray-200 flex-shrink-0" />
                            )}
                            <span className={item.present ? 'text-gray-900' : 'text-gray-500'}>
                              {item.label}
                            </span>
                            {item.source_class === 'claude' && (
                              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                Claude
                              </span>
                            )}
                          </span>
                          <span className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs text-gray-500 min-w-[80px] text-right">
                              {item.present && item.filled_by ? (
                                item.filled_by.source_url ? (
                                  <a
                                    href={item.filled_by.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline"
                                    title={`Source: ${formatSourceLabel(item.filled_by.source_type)}`}
                                  >
                                    {formatSourceLabel(item.filled_by.source_type)}
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span title={`Source: ${formatSourceLabel(item.filled_by.source_type)}`}>
                                    {formatSourceLabel(item.filled_by.source_type)}
                                  </span>
                                )
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500 w-10 text-right">{item.weight}%</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Loading completion checklist…</p>
                )}
              </div>
            )}

            {activeTab === 'dossier' && (
              <div>
                {company.ai_company_dossier ? (
                  <>
                    {company.dossier_generated_at && (
                      <p className="text-xs text-gray-500 mb-3">
                        Generated {new Date(company.dossier_generated_at).toLocaleString()}
                      </p>
                    )}
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
                      {company.ai_company_dossier}
                    </div>
                  </>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-blue-900 mb-1">
                      No dossier generated yet
                    </h4>
                    <p className="text-sm text-blue-900 leading-relaxed">
                      The AI dossier is a Claude-written one-page intelligence brief —
                      what the business does, traction signals, market position, team
                      strength, and risks — distilled from every source the research
                      pipeline pulled. It runs in the background once Claude AI is
                      enabled.
                    </p>
                    <p className="text-sm text-blue-900 leading-relaxed mt-2">
                      <strong>Why it's empty for this company:</strong> Claude is
                      currently disabled platform-wide to control costs. Once you
                      enable it, the dossier will be generated automatically on the
                      next research pass.
                    </p>
                    <div className="mt-4 flex gap-2 flex-wrap">
                      <a
                        href="/ai-settings"
                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                      >
                        Open AI Settings
                      </a>
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/companies/${companyId}/research`);
                            toast.success('Research re-queued. Dossier will populate if Claude is enabled.');
                          } catch (e: any) {
                            toast.error(e?.response?.data?.detail || 'Re-queue failed');
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-50"
                      >
                        Re-run research
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyDetailPage;
